import { MeterPlacement } from '@enmon/commons/lib/enmon';
import { Commodity, isCommodity } from '@enmon/commons/lib/shared';
import { INVENTORIZATION_BLOCK_IDS } from '@/features/inventorization/domain/inventorization';
import {
  getInventorizationIdentityBlockStatus,
  validateInventorizationIdentity,
} from '@/features/inventorization/domain/inventorization-identity-validation';
import { INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS } from '@/features/inventorization/domain/review/inventorize-review-field-ids';
import type { CaptureSession } from '@/shared/capture/capture-session';
import { translateInventoryText } from '@/shared/i18n/inventory-i18n';

export {
  getInventorizationIdentityBlockStatus,
  validateInventorizationIdentity,
} from '@/features/inventorization/domain/inventorization-identity-validation';

export type MeterPlacementSelection = MeterPlacement.Main | MeterPlacement.Secondary | '';

export const INVENTORIZATION_NEW_METER_SELECTIONS = {
  existing: 'false',
  newSubmeterSuggestion: 'true',
} as const;

export type InventorizationNewMeterSelection =
  (typeof INVENTORIZATION_NEW_METER_SELECTIONS)[keyof typeof INVENTORIZATION_NEW_METER_SELECTIONS];

export interface InventorizationIdentity {
  apiKey: string;
  calibrated: string;
  commodity: Commodity | '';
  manufacturer: string;
  manufactured: string;
  meterModel: string;
  newMeter: boolean;
  placement: MeterPlacementSelection;
  serial: string;
}

export function readInventorizationIdentity(session: CaptureSession): InventorizationIdentity {
  const inventorization = session.data.inventorization as { identity?: Partial<InventorizationIdentity> } | undefined;
  const identity = inventorization?.identity ?? {};

  return {
    apiKey: identity.apiKey ?? '',
    calibrated: identity.calibrated ?? '',
    commodity: readCommodity(identity.commodity),
    manufacturer: identity.manufacturer ?? '',
    manufactured: identity.manufactured ?? '',
    meterModel: identity.meterModel ?? '',
    newMeter: identity.newMeter === true,
    placement: readMeterPlacementSelection(identity.placement),
    serial: identity.serial ?? '',
  };
}

export function updateInventorizationIdentity(
  session: CaptureSession,
  patch: Partial<InventorizationIdentity>,
  updatedAt: string,
): CaptureSession {
  const previousIdentity = readInventorizationIdentity(session);
  const identity = {
    ...previousIdentity,
    ...patch,
  };
  const issues = validateInventorizationIdentity(identity);
  const identityStatus = getInventorizationIdentityBlockStatus(identity);

  return {
    ...session,
    blockStatuses: {
      ...session.blockStatuses,
      [INVENTORIZATION_BLOCK_IDS.identity]: identityStatus,
    },
    data: {
      ...session.data,
      inventorization: {
        ...(session.data.inventorization as object),
        identity,
      },
    },
    updatedAt,
    validationErrors: [
      ...session.validationErrors.filter(
        issue =>
          issue.blockId !== INVENTORIZATION_BLOCK_IDS.identity ||
          (issue.fieldId === INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS.apiKey &&
            identity.apiKey === previousIdentity.apiKey),
      ),
      ...issues,
    ],
  };
}

export function readMeterPlacementSelection(value: unknown): MeterPlacementSelection {
  if (value === MeterPlacement.Main) {
    return MeterPlacement.Main;
  }

  if (value === MeterPlacement.Secondary) {
    return MeterPlacement.Secondary;
  }

  return '';
}

export function formatInventorizationPlacement(value: MeterPlacementSelection) {
  if (value === MeterPlacement.Main) {
    return translateInventoryText('app.inventory.identity.placement.mainMeter', 'Main meter');
  }

  if (value === MeterPlacement.Secondary) {
    return translateInventoryText('app.inventory.identity.placement.submeter', 'Submeter');
  }

  return '';
}

export function readCommodity(value: unknown): Commodity | '' {
  return value === '' || isCommodity(value) ? value : '';
}

export function formatInventorizationCommodity(value: Commodity | '') {
  if (value === Commodity.NaturalGas) {
    return translateInventoryText('app.inventory.identity.commodity.gas', 'Gas');
  }

  if (value === Commodity.Electricity) {
    return translateInventoryText('app.inventory.identity.commodity.electricity', 'Electricity');
  }

  if (value === Commodity.Water) {
    return translateInventoryText('app.inventory.identity.commodity.water', 'Water');
  }

  if (value === Commodity.Thermal) {
    return translateInventoryText('app.inventory.identity.commodity.thermal', 'Thermal');
  }

  if (value === Commodity.Other) {
    return translateInventoryText('app.inventory.identity.commodity.other', 'Other');
  }

  return value;
}

export function formatInventorizationNewMeterSelection(newMeter: boolean): InventorizationNewMeterSelection {
  return newMeter
    ? INVENTORIZATION_NEW_METER_SELECTIONS.newSubmeterSuggestion
    : INVENTORIZATION_NEW_METER_SELECTIONS.existing;
}

export function readInventorizationNewMeterSelection(value: unknown) {
  return value === INVENTORIZATION_NEW_METER_SELECTIONS.newSubmeterSuggestion;
}
